import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Section, StatCard, Spinner } from "@/components/ui-parts";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { ClipboardCheck, Camera, Download, TrendingUp, Trash2, GitCompare } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const COLORS = {
  Compliant: "#10B981",
  Partial: "#F59E0B",
  "Non-compliant": "#EF4444",
  "Not Assessed": "#64748B",
};

export default function FrameworkReportPage() {
  const { user } = useAuth();
  const { withScope, activeTenantId } = useTenant();
  const [frameworks, setFrameworks] = useState([]);
  const [selected, setSelected] = useState("iso27001");
  const [report, setReport] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [compare, setCompare] = useState({ a: "", b: "", result: null });
  const canWrite = user?.role !== "viewer";

  useEffect(() => {
    api.get("/reference/frameworks").then(({ data }) => setFrameworks(data));
  }, []);

  const loadAll = () => {
    if (!selected) return;
    setLoading(true);
    Promise.all([
      api.get("/dashboard/framework-report", { params: { ...withScope(), framework: selected } }),
      api.get(`/snapshots/framework/${selected}`, { params: withScope() }),
    ])
      .then(([a, b]) => { setReport(a.data); setSnapshots(b.data); })
      .catch(() => { setReport(null); setSnapshots([]); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [selected, activeTenantId]);

  const saveSnapshot = async () => {
    setSaving(true);
    try {
      await api.post(`/snapshots/framework/${selected}`, null, { params: withScope() });
      loadAll();
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const deleteSnapshot = async (id) => {
    if (!window.confirm("Delete this snapshot?")) return;
    try { await api.delete(`/snapshots/${id}`); loadAll(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const runCompare = async () => {
    if (!compare.a || !compare.b || compare.a === compare.b) { alert("Farklı iki snapshot seçin"); return; }
    try {
      const { data } = await api.get(`/snapshots/${compare.a}/compare/${compare.b}`);
      setCompare({ ...compare, result: data });
    } catch (e) { alert(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ framework: selected });
      const scope = withScope();
      if (scope.tenant_id) params.set("tenant_id", scope.tenant_id);
      const res = await fetch(`${BASE}/api/dashboard/framework-report/pdf?${params}`, {
        credentials: "include",
        headers: localStorage.getItem("gacrf_token") ? { Authorization: `Bearer ${localStorage.getItem("gacrf_token")}` } : {},
      });
      if (!res.ok) throw new Error(`PDF download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GACRF_${selected}_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    finally { setDownloading(false); }
  };

  const pieData = report ? [
    { name: "Compliant", value: report.compliant_count },
    { name: "Partial", value: report.partial_count },
    { name: "Non-compliant", value: report.noncompliant_count },
    { name: "Not Assessed", value: report.not_assessed_count },
  ].filter((s) => s.value > 0) : [];

  const trendData = snapshots.map((s) => ({
    date: s.timestamp.slice(0, 10),
    timestamp: s.timestamp,
    compliance: s.compliance_pct,
    avg: s.overall_avg_score,
    id: s.id,
  }));

  return (
    <div className="p-8 max-w-[1800px]" data-testid="framework-report-page">
      <PageHeader
        eyebrow="Reports"
        title="Framework Compliance Report"
        description="Framework-specific compliance report grouped by control identifier. Save snapshots to track compliance over time and export the report as PDF."
      />

      <div className="card p-4 mb-6" data-testid="framework-selector">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-2">Select Framework</div>
            <div className="flex flex-wrap gap-2">
              {frameworks.map((f) => (
                <button key={f.id} onClick={() => setSelected(f.id)}
                  className={`btn btn-sm ${selected === f.id ? "btn-primary" : "btn-secondary"}`}
                  data-testid={`fw-btn-${f.id}`}>
                  <ClipboardCheck size={12} />{f.name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 items-end">
            {canWrite && (
              <button className="btn btn-secondary btn-sm" onClick={saveSnapshot} disabled={saving || !report} data-testid="save-snapshot-btn">
                <Camera size={12} /> {saving ? "Saving..." : "Save Snapshot"}
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={downloadPdf} disabled={downloading || !report} data-testid="export-pdf-btn">
              <Download size={12} /> {downloading ? "Exporting..." : "Export PDF"}
            </button>
          </div>
        </div>
      </div>

      {loading && <Spinner label="Generating framework report..." />}

      {!loading && report && (
        <>
          <Section title={report.framework_name} description={report.framework_description}>
            <div className="p-5 grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard tone="brand" label="Overall Compliance" value={`${report.compliance_pct}%`} subValue={`${report.compliant_count} compliant controls`} />
              <StatCard label="Total Controls" value={report.total_controls} subValue={`${report.mapped_requirements} requirements`} />
              <StatCard label="Compliant" value={report.compliant_count} tone="success" subValue="Avg ≥ 3.5" />
              <StatCard label="Partial" value={report.partial_count} tone="warning" subValue="2.5 ≤ Avg < 3.5" />
              <StatCard label="Non-compliant" value={report.noncompliant_count} tone="critical" subValue="Avg < 2.5" />
            </div>
          </Section>

          {/* Trend chart */}
          <Section title="Compliance Trend" description={`Compliance % over time (${snapshots.length} snapshot${snapshots.length === 1 ? "" : "s"}). Save a snapshot after each assessment cycle to build history.`}>
            {trendData.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-500">
                <TrendingUp size={24} className="mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
                Henüz snapshot yok. Above &quot;Save Snapshot&quot; butonuna basarak bu framework için ilk zaman noktasını kaydedin.
              </div>
            ) : (
              <>
                <div className="p-5" style={{ height: 260, minHeight: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 10, right: 24, bottom: 10, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                      <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                      <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} label={{ value: "%", angle: -90, position: "insideLeft", style: { fill: "#64748B", fontSize: 10 } }} />
                      <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", fontSize: 12 }} />
                      <Line type="monotone" dataKey="compliance" stroke="#00E5FF" strokeWidth={2} dot={{ fill: "#00E5FF", r: 4 }} name="Compliance %" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto border-t border-slate-800/70">
                  <table className="data-table" data-testid="snapshots-table">
                    <thead><tr><th>Date</th><th>Source</th><th>Compliance</th><th>Overall Avg</th><th>Compliant</th><th>Partial</th><th>Non-compliant</th><th>Not Assessed</th><th>Saved By</th><th></th></tr></thead>
                    <tbody>
                      {snapshots.slice().reverse().map((s) => (
                        <tr key={s.id}>
                          <td className="mono text-[11px]">{new Date(s.timestamp).toLocaleString()}</td>
                          <td><span className={`badge ${s.source === "auto" ? "badge-info" : "badge-brand"}`}>{s.source === "auto" ? "AUTO" : "MANUAL"}</span></td>
                          <td className="mono font-semibold text-slate-100">{s.compliance_pct}%</td>
                          <td className="mono">{s.overall_avg_score.toFixed(2)}</td>
                          <td className="mono">{s.compliant_count}</td>
                          <td className="mono">{s.partial_count}</td>
                          <td className="mono">{s.noncompliant_count}</td>
                          <td className="mono">{s.not_assessed_count}</td>
                          <td className="text-slate-400 text-[11px]">{s.created_by_name || "—"}</td>
                          <td>
                            {(user?.role === "admin" || user?.role === "superadmin") && (
                              <button className="btn btn-danger btn-sm" onClick={() => deleteSnapshot(s.id)} data-testid={`del-snap-${s.id}`}>
                                <Trash2 size={11} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Section>

          {/* Compare Snapshots */}
          {snapshots.length >= 2 && (
            <Section title="Compare Snapshots" description="İki snapshot arasındaki delta'yı hesaplayın (aynı framework, kronolojik sıra otomatik).">
              <div className="p-5">
                <div className="flex flex-wrap items-end gap-2" data-testid="compare-controls">
                  <div className="min-w-[200px]">
                    <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Snapshot A</label>
                    <select value={compare.a} onChange={(e) => setCompare({ ...compare, a: e.target.value, result: null })} data-testid="compare-a">
                      <option value="">— seçin —</option>
                      {snapshots.map((s) => <option key={s.id} value={s.id}>{s.timestamp.slice(0, 19)} ({s.source || "manual"}) · {s.compliance_pct}%</option>)}
                    </select>
                  </div>
                  <div className="min-w-[200px]">
                    <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Snapshot B</label>
                    <select value={compare.b} onChange={(e) => setCompare({ ...compare, b: e.target.value, result: null })} data-testid="compare-b">
                      <option value="">— seçin —</option>
                      {snapshots.map((s) => <option key={s.id} value={s.id}>{s.timestamp.slice(0, 19)} ({s.source || "manual"}) · {s.compliance_pct}%</option>)}
                    </select>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={runCompare} data-testid="compare-run">
                    <GitCompare size={12} /> Compare
                  </button>
                </div>

                {compare.result && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="compare-result">
                    <DeltaCard label="Compliance %" delta={compare.result.delta.compliance_pct} suffix="%"
                      from={compare.result.from.compliance_pct} to={compare.result.to.compliance_pct} />
                    <DeltaCard label="Avg Score" delta={compare.result.delta.avg_score} decimals={2}
                      from={compare.result.from.avg_score} to={compare.result.to.avg_score} />
                    <DeltaCard label="Compliant Controls" delta={compare.result.delta.compliant}
                      from={compare.result.from.compliant} to={compare.result.to.compliant} />
                    <DeltaCard label="Partial" delta={compare.result.delta.partial}
                      from={compare.result.from.partial} to={compare.result.to.partial} invertColors />
                    <DeltaCard label="Non-compliant" delta={compare.result.delta.noncompliant}
                      from={compare.result.from.noncompliant} to={compare.result.to.noncompliant} invertColors />
                    <DeltaCard label="Not Assessed" delta={compare.result.delta.not_assessed}
                      from={compare.result.from.not_assessed} to={compare.result.to.not_assessed} invertColors />
                  </div>
                )}
              </div>
            </Section>
          )}

          {pieData.length > 0 && (
            <Section title="Compliance Distribution">
              <div className="p-5" style={{ height: 260, minHeight: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}
                      label={(e) => `${e.name}: ${e.value}`}>
                      {pieData.map((s) => <Cell key={s.name} fill={COLORS[s.name]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", fontSize: 12 }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}

          <Section title="Controls Detail" description={`Aggregated view of ${report.total_controls} distinct control identifiers.`}>
            <div className="overflow-x-auto" style={{ maxHeight: "70vh" }}>
              <table className="data-table" data-testid="controls-table">
                <thead>
                  <tr>
                    <th>Control ID</th><th>Domains Touched</th><th>Mapped Reqs</th>
                    <th>Assessed</th><th>Avg Score</th><th>High Risk</th>
                    <th>Status</th><th>Sample Gaps</th>
                  </tr>
                </thead>
                <tbody>
                  {report.controls.map((c) => (
                    <tr key={c.controlId} data-testid={`ctrl-${c.controlId}`}>
                      <td className="mono font-semibold text-slate-100">{c.controlId}</td>
                      <td className="text-slate-400 text-[11px]">{c.domains.join(" · ")}</td>
                      <td className="mono">{c.mappedRequirements}</td>
                      <td className="mono">{c.assessed}</td>
                      <td className="mono">{c.avgScore.toFixed(2)}</td>
                      <td className="mono">{c.highRiskCount}</td>
                      <td>
                        <span className={`badge ${
                          c.complianceStatus === "Compliant" ? "badge-success"
                          : c.complianceStatus === "Partial" ? "badge-medium"
                          : c.complianceStatus === "Non-compliant" ? "badge-critical"
                          : "badge-low"}`}>{c.complianceStatus}</span>
                      </td>
                      <td className="text-slate-400 text-[11px] max-w-md">
                        {c.sampleGaps?.length ? c.sampleGaps.map((g, i) => <div key={i}>• {g}</div>) : <span className="text-slate-600">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}

      {!loading && !report && selected && (
        <div className="card p-8 text-center text-slate-500 text-sm">
          Bu framework için henüz veri yok veya seçilen tenant&apos;ta atanmış kontrol bulunamadı.
        </div>
      )}
    </div>
  );
}

function DeltaCard({ label, delta, from, to, decimals = 0, suffix = "", invertColors }) {
  const isUp = delta > 0;
  const isDown = delta < 0;
  const good = invertColors ? isDown : isUp;
  const bad = invertColors ? isUp : isDown;
  const color = good ? "text-emerald-400" : bad ? "text-red-400" : "text-slate-400";
  const arrow = isUp ? "▲" : isDown ? "▼" : "—";
  const fmt = (n) => decimals ? n.toFixed(decimals) : n;
  return (
    <div className="card p-3">
      <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</div>
      <div className={`font-mono text-2xl font-semibold ${color}`}>
        {arrow} {isUp ? "+" : ""}{fmt(delta)}{suffix}
      </div>
      <div className="text-[10px] font-mono text-slate-500 mt-1">
        {fmt(from)}{suffix} → {fmt(to)}{suffix}
      </div>
    </div>
  );
}
